import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import { 
  Warning as WarningIcon, 
  Error as ErrorIcon, 
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon 
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

const InvitationAccept = ({ token, onAccepted, onError }) => {
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvitationDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/invitations/${token}`, {
        method: 'GET',
        requireAuth: false // This endpoint doesn't require authentication
      });
      setInvitation(data);
    } catch (error) {
      console.error('Error fetching invitation details:', error);
      setError('Invalid or expired invitation link');
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  }, [token, onError]);

  useEffect(() => {
    fetchInvitationDetails();
  }, [token, fetchInvitationDetails]);

  const handleAcceptInvitation = async () => {
    try {
      setAccepting(true);
      await apiCall(`/api/invitations/${token}/accept`, {
        method: 'POST'
      });
      
      // Invitation accepted successfully
      onAccepted && onAccepted();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Loading invitation details...
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
              <Avatar sx={{ bgcolor: 'error.main', width: 64, height: 64 }}>
                <WarningIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h5" component="h2" color="error.main">
                Invitation Error
              </Typography>
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => window.location.href = '/'}
                size="large"
              >
                Go to Home
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!invitation) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
              <Avatar sx={{ bgcolor: 'error.main', width: 64, height: 64 }}>
                <ErrorIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h5" component="h2" color="error.main">
                Invitation Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                This invitation link is invalid or has expired.
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.location.href = '/'}
                size="large"
              >
                Go to Home
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Stack spacing={3} sx={{ py: 2 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                <GroupIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h4" component="h1" gutterBottom>
                Family Invitation
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You've been invited to join a family on Life Runbooks
              </Typography>
            </Box>

            <Divider />

            {/* Invitation Details */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Invitation Details
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Family Name:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {invitation.familyName || 'Unnamed Family'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Invited By:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {invitation.invitedBy || 'Unknown User'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Role:
                  </Typography>
                  <Chip 
                    label={invitation.role || 'Member'} 
                    color="primary" 
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {invitation.expiresAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Expires:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(invitation.expiresAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Actions */}
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                By accepting this invitation, you'll be able to:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    Access shared family runbooks and procedures
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    Collaborate with family members on emergency plans
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2">
                    Receive family-wide notifications and updates
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            <Divider />

            {/* Accept Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleAcceptInvitation}
                disabled={accepting}
                startIcon={accepting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                sx={{ minWidth: 200 }}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                You can leave the family at any time from your account settings.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default InvitationAccept;